import { logger } from '../utils/logger';

// TypeScript declarations for Goftino API
interface GoftinoUser {
  name: string;
  about: string;
  phone: string;
  forceUpdate: boolean;
}

interface GoftinoAPI {
  setUser(user: GoftinoUser): void;
  getUserId(): string;
  setUserId(id: string, callback: (result: any) => void): void;
}

declare global {
  interface Window {
    Goftino?: GoftinoAPI;
  }
}

class GoftinoService {
  /**
   * Check if Goftino widget is available
   */
  static isAvailable(): boolean {
    return typeof window !== 'undefined' && !!window.Goftino;
  }

  /**
   * Set user information in Goftino
   */
  static setUser(user: GoftinoUser): void {
    try {
      if (!this.isAvailable()) {
        logger.warn('Goftino not available, skipping user setup', 'GoftinoService');
        return;
      }

      window.Goftino!.setUser(user);
      logger.info('Goftino user info updated', 'GoftinoService', {
        name: user.name,
        about: user.about,
        hasPhone: !!user.phone
      });
    } catch (error) {
      logger.error('Failed to set Goftino user', 'GoftinoService', error);
    }
  }

  /**
   * Clear user information (for logout)
   */
  static clearUser(): void {
    try {
      if (!this.isAvailable()) {
        logger.warn('Goftino not available, skipping clear user', 'GoftinoService');
        return;
      }

      // Clear user by setting empty values
      window.Goftino!.setUser({
        name: '',
        about: '',
        phone: '',
        forceUpdate: true
      });
      logger.info('Goftino user info cleared', 'GoftinoService');
    } catch (error) {
      logger.error('Failed to clear Goftino user', 'GoftinoService', error);
    }
  }

  /**
   * Wait for Goftino to be ready
   */
  static waitForReady(): Promise<void> {
    return new Promise((resolve) => {
      try {
        // If Goftino is already available, resolve immediately
        if (this.isAvailable()) {
          logger.info('Goftino already available', 'GoftinoService');
          resolve();
          return;
        }

        // Otherwise, wait for the goftino_ready event
        const handleReady = () => {
          logger.info('Goftino ready event received', 'GoftinoService');
          window.removeEventListener('goftino_ready', handleReady);
          resolve();
        };

        window.addEventListener('goftino_ready', handleReady);

        // Timeout after 10 seconds if Goftino doesn't load
        setTimeout(() => {
          window.removeEventListener('goftino_ready', handleReady);
          logger.warn('Goftino ready timeout - widget may not be loaded', 'GoftinoService');
          resolve(); // Resolve anyway to not block the application
        }, 10000);
      } catch (error) {
        logger.error('Error waiting for Goftino ready', 'GoftinoService', error);
        resolve(); // Resolve anyway to not block the application
      }
    });
  }
}

export default GoftinoService;
